import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Bell, 
  Shield, 
  CreditCard, 
  User,
  Check
} from 'lucide-react';
import './SettingsPage.css';

// ============================================
// TOGGLE SWITCH COMPONENT
// ============================================
const ToggleSwitch = ({ enabled, onChange, label }) => (
  <button 
    className={`toggle-switch ${enabled ? 'enabled' : ''}`}
    onClick={() => onChange(!enabled)}
    aria-label={label}
  >
    <div className="toggle-track">
      <div className="toggle-thumb" />
    </div>
  </button>
);

// ============================================
// SETTINGS CARD COMPONENT
// ============================================
const SettingsCard = ({ icon: Icon, title, description, children }) => (
  <div className="settings-card">
    <div className="settings-card-header">
      <div className="settings-card-icon">
        <Icon size={22} />
      </div>
      <div className="settings-card-info">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
    <div className="settings-card-content">
      {children}
    </div>
  </div>
);

// ============================================
// SETTINGS ROW COMPONENT
// ============================================
const SettingsRow = ({ label, description, children }) => (
  <div className="settings-row">
    <div className="settings-row-info">
      <span className="settings-row-label">{label}</span>
      {description && <span className="settings-row-desc">{description}</span>}
    </div>
    <div className="settings-row-action">
      {children}
    </div>
  </div>
);

// ============================================
// MAIN SETTINGS PAGE COMPONENT
// ============================================
function SettingsPage() {
  const navigate = useNavigate();

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('elemental-settings');
    return saved ? JSON.parse(saved) : {
      emailNotifications: true,
      pushNotifications: false,
      priceAlerts: true,
      newsAlerts: true,
      earningsAlerts: true,
      twoFactorAuth: false,
      biometricLogin: false,
      language: 'English',
      timezone: 'EST'
    };
  });

  const updateSetting = (key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem('elemental-settings', JSON.stringify(newSettings));
      return newSettings;
    });
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-content">
          <Settings size={28} className="settings-header-icon" />
          <div>
            <h1>Settings</h1>
            <p>Manage your account and preferences</p>
          </div>
        </div>
      </div>

      <div className="settings-container">
        {/* General Settings */}
        <SettingsCard 
          icon={User} 
          title="General Settings" 
          description="Language and display preferences"
        >
          <SettingsRow label="Language">
            <select 
              className="settings-select"
              value={settings.language}
              onChange={(e) => updateSetting('language', e.target.value)}
            >
              <option value="English">English</option>
              <option value="Spanish">Español</option>
              <option value="French">Français</option>
              <option value="German">Deutsch</option>
              <option value="Japanese">日本語</option>
            </select>
          </SettingsRow>

          <SettingsRow label="Timezone">
            <select 
              className="settings-select"
              value={settings.timezone}
              onChange={(e) => updateSetting('timezone', e.target.value)}
            >
              <option value="EST">Eastern (EST)</option>
              <option value="CST">Central (CST)</option>
              <option value="MST">Mountain (MST)</option>
              <option value="PST">Pacific (PST)</option>
              <option value="UTC">UTC</option>
            </select>
          </SettingsRow>
        </SettingsCard>

        {/* Notifications */}
        <SettingsCard 
          icon={Bell} 
          title="Notifications" 
          description="Manage how you receive alerts and updates"
        >
          <SettingsRow label="Email Notifications" description="Receive updates via email">
            <ToggleSwitch 
              enabled={settings.emailNotifications} 
              onChange={(v) => updateSetting('emailNotifications', v)}
              label="Email Notifications"
            />
          </SettingsRow>

          <SettingsRow label="Push Notifications" description="Browser and mobile alerts">
            <ToggleSwitch 
              enabled={settings.pushNotifications} 
              onChange={(v) => updateSetting('pushNotifications', v)}
              label="Push Notifications"
            />
          </SettingsRow>

          <SettingsRow label="Price Alerts" description="Get notified on price movements">
            <ToggleSwitch 
              enabled={settings.priceAlerts} 
              onChange={(v) => updateSetting('priceAlerts', v)}
              label="Price Alerts"
            />
          </SettingsRow>

          <SettingsRow label="News Alerts" description="Breaking news for your watchlist">
            <ToggleSwitch 
              enabled={settings.newsAlerts} 
              onChange={(v) => updateSetting('newsAlerts', v)}
              label="News Alerts"
            />
          </SettingsRow>

          <SettingsRow label="Earnings Alerts" description="Upcoming earnings reminders">
            <ToggleSwitch 
              enabled={settings.earningsAlerts} 
              onChange={(v) => updateSetting('earningsAlerts', v)}
              label="Earnings Alerts"
            />
          </SettingsRow>
        </SettingsCard>

        {/* Security */}
        <SettingsCard 
          icon={Shield} 
          title="Security" 
          description="Protect your account with advanced security"
        >
          <SettingsRow label="Two-Factor Authentication" description="Add an extra layer of security">
            <ToggleSwitch 
              enabled={settings.twoFactorAuth} 
              onChange={(v) => updateSetting('twoFactorAuth', v)}
              label="Two-Factor Auth"
            />
          </SettingsRow>

          <SettingsRow label="Biometric Login" description="Use fingerprint or face ID">
            <ToggleSwitch 
              enabled={settings.biometricLogin} 
              onChange={(v) => updateSetting('biometricLogin', v)}
              label="Biometric Login"
            />
          </SettingsRow>

          <SettingsRow label="Change Password">
            <button className="settings-btn-secondary">
              Update
            </button>
          </SettingsRow>

          <SettingsRow label="Active Sessions">
            <button className="settings-btn-secondary">
              Manage
            </button>
          </SettingsRow>
        </SettingsCard>

        {/* Subscription */}
        <SettingsCard 
          icon={CreditCard} 
          title="Subscription" 
          description="Manage your plan and billing"
        >
          <div className="subscription-info">
            <div className="current-plan">
              <div className="plan-badge">FREE</div>
              <div className="plan-details">
                <h4>Free Plan</h4>
                <p>Basic features • Upgrade for more</p>
              </div>
            </div>
            <div className="plan-features">
              <div className="feature-item">
                <Check size={16} className="feature-check" />
                <span>5 AI Analyses per day</span>
              </div>
              <div className="feature-item">
                <Check size={16} className="feature-check" />
                <span>Basic Charts</span>
              </div>
              <div className="feature-item limited">
                <Check size={16} className="feature-check" />
                <span>Limited Watchlist</span>
              </div>
              <div className="feature-item limited">
                <Check size={16} className="feature-check" />
                <span>Delayed Data</span>
              </div>
            </div>
          </div>

          <div className="subscription-actions">
            <button className="settings-btn-primary" onClick={() => navigate('/pricing')}>
              Upgrade to Pro
            </button>
            <button className="settings-btn-secondary" onClick={() => navigate('/pricing')}>
              View Plans
            </button>
          </div>
        </SettingsCard>

        {/* Danger Zone */}
        <div className="settings-card danger-zone">
          <div className="settings-card-header">
            <div className="settings-card-icon danger">
              <Shield size={22} />
            </div>
            <div className="settings-card-info">
              <h3>Danger Zone</h3>
              <p>Irreversible account actions</p>
            </div>
          </div>
          <div className="settings-card-content">
            <SettingsRow label="Export Data" description="Download all your data">
              <button className="settings-btn-secondary">
                Export
              </button>
            </SettingsRow>
            <SettingsRow label="Delete Account" description="Permanently delete your account">
              <button className="settings-btn-danger">
                Delete
              </button>
            </SettingsRow>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;